import SelectionButton from '@/components/sideNavigation/Cs/chatMessage/SelectionButton';
import { Icon } from '@successmode-ktp/kui';
import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

type Props = {
  id: string;
  title: string;
  selected: { label: string; value: string };
  onChange: ({ label, value }: { label: string; value: string }) => void;
  options: { label: string; value: string }[];
  placeholder: string;
  isOpenDropdown: boolean;
  setIsOpenDropdown: (value: boolean) => void;
};

function Selection({
  id,
  title,
  selected,
  onChange,
  options,
  placeholder,
  isOpenDropdown,
  setIsOpenDropdown,
}: Props) {
  const SelectionRef = useRef<HTMLDivElement>(null);
  const DropdownRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState('');
  const [focusedValue, setFocusedValue] = useState({ label: '', value: '' });

  const optionResults = useMemo(
    () =>
      search !== ''
        ? options.filter(({ label, value }) => {
            return label?.toLowerCase().indexOf(search?.toLowerCase()) > -1;
          })
        : options,
    [search],
  );

  const onToggleButton = () => {
    setIsOpenDropdown(!isOpenDropdown);
    setSearch(selected.label);
  };

  const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const searchValue =
      id === 'nationality'
        ? value.replace(/[^a-zA-Z]/g, '').toUpperCase()
        : value;
    setSearch(searchValue);

    if (!isOpenDropdown) {
      setIsOpenDropdown(true);
    }
  };

  const onSelectNation = ({
    label,
    value,
  }: {
    label: string;
    value: string;
  }) => {
    onChange({ label, value });
    setSearch(label);
    setFocusedValue({ label: '', value: '' });
    setIsOpenDropdown(!isOpenDropdown);
  };

  const onEnter = (e: KeyboardEvent) => {
    e.preventDefault();
    if (e.code === 'Enter' && !focusedValue) {
      setIsOpenDropdown(!isOpenDropdown);
    }
  };

  const onKeyDown = useCallback(
    (e: any) => {
      const { code } = e;
      if (['ArrowDown', 'ArrowUp'].includes(code)) {
        let idx = optionResults.findIndex(
          (option) => option.value === focusedValue.value,
        );
        if (code === 'ArrowDown') {
          idx = idx >= optionResults.length - 1 ? 0 : idx + 1;
        } else if (code === 'ArrowUp') {
          idx = idx <= 0 ? optionResults.length - 1 : idx - 1;
        }
        setFocusedValue(optionResults[idx]);
      } else if (code === 'Enter') {
        if (optionResults.length === 1) {
          const selectedOption = optionResults[0];
          onSelectNation(selectedOption);
          setFocusedValue({ label: '', value: '' });
          setIsOpenDropdown(false);
          return;
        }
        if (focusedValue) {
          onSelectNation(focusedValue);
          setFocusedValue({ label: '', value: '' });
        }
      } else if (code === 'Escape' || code === 'Tab') {
        setIsOpenDropdown(false);
        setFocusedValue({ label: '', value: '' });
      }
    },
    [DropdownRef, focusedValue, optionResults],
  );

  useEffect(() => {
    const currentSection = document.querySelector(`#${id}`);
    if (isOpenDropdown && currentSection) {
      currentSection.addEventListener('keydown', onKeyDown);
    }

    return () => {
      if (currentSection) {
        currentSection.removeEventListener('keydown', onKeyDown);
      }
    };
  }, [isOpenDropdown, onKeyDown, optionResults]);

  return (
    <>
      <Label>
        {title} <span>*</span>
      </Label>
      <div ref={SelectionRef} id={id}>
        <Wrapper isOpen={isOpenDropdown}>
          <Input
            value={search}
            onChange={onSearch}
            placeholder={placeholder}
            onBlur={() => {
              setSearch(selected.label);
              setIsOpenDropdown(false);
            }}
            onFocus={() => setIsOpenDropdown(true)}
          />
          <ToggleButton
            onClick={onToggleButton}
            isOpen={isOpenDropdown}
            onKeyDown={onEnter}
          >
            <Icon.ChevronDown size={24} fillColor='#5F6165' />
          </ToggleButton>
        </Wrapper>
        {isOpenDropdown &&
          (optionResults.length > 0 ? (
            <Dropdown ref={DropdownRef}>
              {optionResults.map(({ label, value }) => (
                <SelectionButton
                  key={value}
                  label={label}
                  isSelected={value === selected.value}
                  onClick={() => onSelectNation({ label, value })}
                  focused={value === focusedValue.value}
                />
              ))}
            </Dropdown>
          ) : (
            <NoResult>검색 결과가 없습니다</NoResult>
          ))}
      </div>
    </>
  );
}

const Label = styled.label`
  margin-bottom: 6px;
  > span {
    color: var(--Secondary-Danger);
  }
`;

const Wrapper = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 36px;
  padding: 9px 12px;
  border-radius: ${({ isOpen }) => (isOpen ? '12px 12px 0 0' : '12px')};
  background-color: #fff;
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  ${({ isOpen }) =>
    isOpen &&
    css`
      border-bottom: 1px solid #e5e6e8;
    `}
`;

const Input = styled.input`
  height: 100%;
  background-color: transparent;
  border: 0;
  outline: 0;
  color: #3a3b3e;

  &::placeholder {
    color: var(--Gray-09);
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
  }
`;

const ToggleButton = styled.button<{ isOpen: boolean }>`
  width: 24px;
  height: 24px;
  padding: 0;
  transform: rotate(${({ isOpen }) => (isOpen ? '180deg' : '0deg')});
  transition: all 200ms;
`;

const Dropdown = styled.div`
  width: 243px;
  max-height: 170px;
  overflow-y: auto;
  background-color: #fff;
  padding-bottom: 10px;
  border-radius: 0 0 12px 12px;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const NoResult = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 243px;
  height: 170px;
  overflow-y: auto;
  background-color: #fff;
  padding-bottom: 10px;
  border-radius: 0 0 12px 12px;

  color: var(--Mono-50, #80848a);
  text-align: center;
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
`;

export default Selection;
